import React, { useState, useEffect, useRef } from 'react';
import styles from './style.module.css';
import { MdOutlinePhoneInTalk, MdOutlineMailOutline } from "react-icons/md";
import { Modal } from 'react-bootstrap';
import Calendly from '../Calendly';
import { WEBApi } from '../../API/WEBApi';
import { useLocation } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import { IoIosArrowDown } from "react-icons/io";
import { MdEdit } from "react-icons/md";



const FooterForm = () => {
  const [consultationModal, setConsultationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    propertyType: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidUSPhone = (phone: string): boolean =>
    /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(phone);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const { email, phone } = formData;

    if (!isValidEmail(email)) {
      setErrorMessage('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }

    if (phone && !isValidUSPhone(phone)) {
      setErrorMessage('Please enter a valid US phone number (e.g., 123-456-7890).');
      setIsSubmitting(false);
      return;
    }

    if (!showCaptcha) {
      setShowCaptcha(true);
      return;
    }

    setIsSubmitting(true);


    if (!recaptchaToken) {
      setErrorMessage('Please complete the CAPTCHA.');
      setIsSubmitting(false);
      return;
    }

    try {
      await WEBApi.createNewContact({
        ...formData,
        recaptchaToken,
      });

      setFormSubmitted(true);
    } catch (error: any) {
      if (error?.response?.status === 400) {
        setErrorMessage('Invalid CAPTCHA. Please try again.');
      } else {
        setErrorMessage('Submission failed. Try again.');
      }

      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      propertyType: '',
      message: '',
    });
    setFormSubmitted(false);
    setRecaptchaToken(null);
    setShowCaptcha(false);
    setErrorMessage(null);

    setTimeout(() => {
      firstNameRef.current?.focus();
    }, 0);
  };

  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#contact-sales') {
      const el = document.getElementById('contact-sales');
      if (el) {
        setTimeout(() => {
          const offsetTop = el.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: offsetTop - 100,
            behavior: 'smooth',
          });
        }, 100);
      }
    }
  }, [location]);

  const readonlyClass = showCaptcha ? styles['readonly-input'] : '';

  return (
    <div className={styles['footer-form-main-container']}>
      <div className={styles['footer-form-left-box']}>
        <span className={styles['pill-header']}>Ready to Get Started?</span>
        <div className={styles['footer-form-left-box-heading']}>
          Transform Your Parking Into a Profitable EV Charging Hub
        </div>
        <div className={styles['footer-form-left-box-para']}>
          Join property owners nationwide who are generating new revenue while
          providing a valuable amenity with BreatheEV's reservation-enabled
          charging solutions.
        </div>
        <div className={styles['footer-form-left-box-contact']}>
          <span><MdOutlinePhoneInTalk /> (844) 252-2083</span>
          <span><MdOutlineMailOutline /> sales@breathe-ev.com</span>
        </div>
        <div className={styles['footer-form-button-container']}>
          <button
            className={styles['footer-form-button']}
            onClick={() => setConsultationModal(true)}
          >
            Schedule a Consultation
          </button>
        </div>
      </div>

      <div className={styles['footer-form-right-box']}>
        {formSubmitted ? (
          <div className={styles['message-holder']}>
            <div className={styles['thank-you-message']}>
              <h3>Welcome to BreatheEV!</h3>
              <p>Thank you for contacting us. We will get back to you shortly.</p>
              <button onClick={resetForm} className={styles['button']}>Back to form</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} id="contact-sales">
            <div className={styles['footer-form-right-box-form-header']}>Get In Touch {showCaptcha && <button onClick={()=>{setShowCaptcha(false)}} className={styles['btn-edit']}><MdEdit /> Edit</button>}</div>

            <div className={styles['form-row-horizontal']}>
              <div className={styles['form-group']}>
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  readOnly={showCaptcha}
                  className={readonlyClass}
                  ref={firstNameRef}
                />
              </div>
              <div className={styles['form-group']}>
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  readOnly={showCaptcha}
                  className={readonlyClass}
                />
              </div>
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                readOnly={showCaptcha}
                className={readonlyClass}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="phone">Phone</label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                readOnly={showCaptcha}
                className={readonlyClass}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="propertyType">Property Type</label>
              <div className={styles['propertyTypeContainer']}>
                <select
                  id="propertyType"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  required
                  disabled={showCaptcha}
                  className={readonlyClass}
                >
                  <option disabled value="">Select a property type</option>
                  <option value="Multi-Family">Multi-Family</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Retail">Retail</option>
                  <option value="Hospitality">Hospitality</option>
                  <option value="University">University</option>
                </select>
                <IoIosArrowDown />
              </div>

            </div>

            <div className={styles['form-group']}>
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={2}
                readOnly={showCaptcha}
                className={readonlyClass}
              ></textarea>
            </div>

            {showCaptcha && (
              <div className={styles['form-group']}>
                <label>Complete CAPTCHA</label>

                  <ReCAPTCHA
                    sitekey="6Lfr5HMrAAAAAL7YP3cT6418M2iezHk7QGZkdIEp"
                    ref={recaptchaRef}
                    onChange={(token) => setRecaptchaToken(token)}
                    onExpired={() => {
                      setRecaptchaToken(null);
                      setErrorMessage("CAPTCHA expired. Please try again.");
                      recaptchaRef.current?.reset();
                    }}
                    onErrored={() => {
                      setErrorMessage("Failed to load CAPTCHA. Please disable ad blockers or retry.");
                    }}
                  />
                  


              </div>
            )}

            {errorMessage && (
              <div className={styles['error-message']}>{errorMessage}</div>
            )}

            <div className={styles['form-group']}>
              <button
                type="submit"
                className={styles['submit-button']}
                disabled={isSubmitting || (showCaptcha && !recaptchaToken)}
              >
                {isSubmitting
                  ? <div className={styles['button-loading']}>
                    Submitting <div className="spinner-border text-light" role="status"></div>
                  </div>
                  : showCaptcha
                    ? 'Submit with CAPTCHA'
                    : 'Submit'
                }
              </button>
            </div>
          </form>
        )}
      </div>

      <Calendly
        consultationModal={consultationModal}
        setConsultationModal={setConsultationModal}
      />
    </div>
  );
};

export default FooterForm;
